import type { Content } from '@prismicio/client';

/**
 * The redesign-era variations of this slice. All are rendered by
 * `variants/Redesign.tsx`, which assembles the shared section shell and then
 * delegates each card to the component that owns that layout.
 *
 * Adding a redesign layout means adding an id here plus its own card
 * component — there is no shared config table to keep in sync.
 */
export const REDESIGN_VARIATIONS = [
  'redesignCardsHorizontal',
  'redesignCardsHorizontalScroll',
  'redesignCardsStacked',
  'redesignParagraphSteps',
] as const;

export type RedesignVariation = (typeof REDESIGN_VARIATIONS)[number];

export type RedesignSlice = Extract<
  Content.HeadlineDescriptionCtaStepsSlice,
  { variation: RedesignVariation }
>;

/**
 * Narrows a slice to the redesign variations, so the parent `index.tsx` can
 * route to the redesign renderer without repeating the variation ids.
 */
export const isRedesignSlice = (
  slice: Content.HeadlineDescriptionCtaStepsSlice,
): slice is RedesignSlice =>
  (REDESIGN_VARIATIONS as readonly string[]).includes(slice.variation);

type VariationSlice<TVariation extends RedesignVariation> = Extract<
  Content.HeadlineDescriptionCtaStepsSlice,
  { variation: TVariation }
>;

/** One card in the full-width stacked layout. */
export type StackedStep =
  VariationSlice<'redesignCardsStacked'>['primary']['steps'][number];

/** One card in the numbered paragraph-steps row. */
export type ParagraphStep =
  VariationSlice<'redesignParagraphSteps'>['primary']['steps'][number];
